<script>
import {
  computedPbxBasePlans,
  computedPbxCallingPlans,
  computedPbxPlans,
  PBX_SUB_TYPES,
} from '@/common/service/computers/plans/pbx';
import mixin from '@/plans/mx/shopping-cart';
import { tollFreeNumberStore, phoneNumberStorage } from './store';
import { plansof } from '@/utils/chain';
import { BUNDLE_WITH_ZP_BASE_PLAN_TYPES, planTypes } from '@/common/constants';
import { isBizPlusContext, isProPlusContext } from '../ZOOM_MEETING/common/utils';
import { phonePlanUnitMap } from './helper/constants';
import { routePathMap } from '@/router/constant';
import { getDefaultPayApmInfo } from '@/store/modules/payment';
import { canRemoveAddOnPlanInWinBack, isFromShoppingCart } from '@/views/config/shopping-cart/persist/plan-remove';
import { pageConfig } from '@/common/service/page-config';
import { mapActions, mapMutations } from 'vuex';

export default {
  mixins: [mixin],
  computed: {
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    pbxBasePlan() {
      return computedPbxBasePlans(this.selectedPbxPlans)[0];
    },
    pbxCallingPlan() {
      const callingPlans = computedPbxCallingPlans(this.selectedPbxPlans);
      return callingPlans.length > 1 ? callingPlans : callingPlans[0];
    },
    pbxTFPlan() {
      const tfPlans = this.selectedPbxPlans.filter((item) => item.sub_type === PBX_SUB_TYPES.TOLLFREE);
      return tfPlans.length > 1 ? tfPlans : tfPlans[0];
    },
    pbxPNPlan() {
      return this.selectedPbxPlans.filter((item) => item.sub_type === PBX_SUB_TYPES.PHONE_NUMBER);
    },
    pbxAddonPlans() {
      return this.selectedPbxPlans.filter((item) =>
        [PBX_SUB_TYPES.FEATURE, PBX_SUB_TYPES.UNLIMITED_ADDON].includes(item.sub_type),
      );
    },
    isShowRmvBtn() {
      if (this.$context.is.checkoutPage) return true;
      return false;
    },
  },
  methods: {
    ...mapMutations({
      batchRemovePlan: 'batchRemovePlan'
    }),
    ...mapActions(['removePlanByKey']),
    mx_edit_addon(plan) {
      if (!this.$context.is.checkoutPage) return;
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.edit')} ${this.$t(plan?.planNameKey)}`}
          onClick={() => {
            this.checkProMonthlyAndYearlyPriceChange();
            this.$router.push({
              path: routePathMap.config,
            });
            if (this.apmInfo.isApm) {
              this.$nextTick(() => {
                this.setApmInfo(getDefaultPayApmInfo());
              });
            }
            this.$tracking.orderSummary(
              `edit ${this.$pkg.components?.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.edit')}
        </ZmButton>
      );
    },
    removeTFPlan(plan) {
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            tollFreeNumberStore.state.pickedNumberList = [];
            phoneNumberStorage.update('tollFreeNumberState', tollFreeNumberStore.state);
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.$pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    removePNPlan(plan) {
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this._dispatch('remove', plan);
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    $renderCallingPlanContent(plan) {
      //todo confirm. Some dependent addOns may cause some plans to be removed sometimes and not removed sometimes
      const canRemove = false && canRemoveAddOnPlanInWinBack(plan.type) && this.pbxAddonPlans.length === 0 && (!this.pbxPNPlan || this.pbxPNPlan.length === 0) && (!this.pbxTFPlan || this.pbxTFPlan.length === 0);
      return (
        <div class="pbx-cart-part">
          {this.mx_subTitle(this.$t(`billing.pbx.cart_name.${plan.category}`), this.mx_price(plan), plan?.plan_code)}
          {this.mx_discount(plan.plan_code)}
          <div class="opc-cart-item__text">
            {plan.cartInfo.selected_number > 1
              ? this.$t('billing.pbx.x_calling_plan', [plan.cartInfo.selected_number])
              : this.$t('billing.pbx.one_calling_plan')}
          </div>
          {this.mx_cycle(plan.billing_cycle)}
          {this.mx_edit(plan)}
          {canRemove && this.remove_calling_plan(plan)}
        </div>
      );
    },
    removeCallingPlanHandle(plan) {
      if (!Array.isArray(this.pbxCallingPlan) || this.pbxCallingPlan.length === 1) {
        this.removePlanByKey(planTypes.ZOOM_PHONE);
        const hasBundleZpPlan = this.getSelectedPlans.some(item => BUNDLE_WITH_ZP_BASE_PLAN_TYPES.includes(item.type));
        if (!hasBundleZpPlan) {
          const zoomPhonePlans = computedPbxPlans(this.getSelectedPlans);
          this.batchRemovePlan(zoomPhonePlans);
        }
      } else {
        this._dispatch('remove', plan);
      }
    },
    remove_calling_plan(plan) {
      if(isFromShoppingCart()) {
        return this.mx_remove_persist_cart(plan);
      }
      if (!this.$context.is.checkoutPage) return;
      const configType = pageConfig?.editConfig?.planType;
      if(configType === plan.type && plansof(this.getSelectedPlans).type(plan.type).has(1)) return; // base plan can't remove if query contains
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this.removeCallingPlanHandle(plan);
            this.$tracking.orderSummary(
              `remove ${this.$pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    $renderTollFreePlanContent(plan) {
      return (
        <div class="pbx-cart-part">
          {this.mx_subTitle(this.$t(`billing.pbx.cart_name.${plan.category}`), this.mx_price(plan), plan?.plan_code)}
          {this.mx_discount(plan.plan_code)}
          {this.mx_license(plan.cartInfo.selected_number, this.$t('billing.planform.unit.number'))}
          {this.mx_cycle(plan.billing_cycle)}
          {this.mx_edit(plan)}
          {this.isShowRmvBtn && this.removeTFPlan(plan)}
        </div>
      );
    },
    $renderCallingPlan() {
      return (
        !!this.pbxCallingPlan &&
        (Array.isArray(this.pbxCallingPlan)
          ? this.pbxCallingPlan.map((plan) => this.$renderCallingPlanContent(plan))
          : this.$renderCallingPlanContent(this.pbxCallingPlan))
      );
    },
    $renderTollFreePlan() {
      return (
        !!this.pbxTFPlan &&
        (Array.isArray(this.pbxTFPlan)
          ? this.pbxTFPlan.map((plan) => this.$renderTollFreePlanContent(plan))
          : this.$renderTollFreePlanContent(this.pbxTFPlan))
      );
    },
    $renderPNplan() {
      return (
        !!this.pbxPNPlan &&
        this.pbxPNPlan.map((plan) => (
          <div class="pbx-cart-part">
            {this.mx_subTitle(this.$t(`billing.pbx.cart_name.${plan.category}`), this.mx_price(plan), plan?.plan_code)}
            {this.mx_discount(plan.plan_code)}
            {this.mx_license(plan.cartInfo.selected_number, this.$t('billing.planform.unit.number'))}
            {this.mx_cycle(plan.billing_cycle)}
            {this.mx_edit(plan)}
            {this.isShowRmvBtn && this.removePNPlan(plan)}
          </div>
        ))
      );
    },
    $renderBasePlan() {
      if (!this.pbxBasePlan) return null;
      return (
        <div class="pbx-cart-part">
          {this.mx_subTitle(
            this.$t('billing.pbx.metered_calling_credits'),
            this.pbxBasePlan.amount,
            this.pbxBasePlan?.plan_code,
          )}
          {this.mx_discount(this.pbxBasePlan.plan_code)}
          {this.mx_cycle(this.pbxBasePlan.billing_cycle)}
          {this.mx_edit(this.pbxCallingPlan)}
        </div>
      );
    },

    mergePlans (planList) {
      if(!Array.isArray(planList)) {
        return [];
      }
      return planList.reduce((acc, cur) => {
        if(Array.isArray(cur)) {
          return [...acc, ...cur];
        }
      }, [])
    },

    getPlans (plans) {
      if(!plans) {
        return [];
      }
      if(Array.isArray(plans)) {
        return plans;
      }
      return [plans];
    },

    renderMainTitle () {
      const callingPlans = this.getPlans(this.pbxCallingPlan);
      const tollFreePlans = this.getPlans(this.pbxTFPlan);
      const pnPlans = this.getPlans(this.pbxPNPlan);
      const basePlans = this.getPlans(this.pbxBasePlan);
      const plans = this.mergePlans([callingPlans, tollFreePlans, pnPlans, basePlans]);
      return plans.map((item, index) => {
        if(!index) {
          return this.mx_main_title(this.$t('billing.planform.title.zoomphone'),  undefined, item.parent_category)
        }
        return <div style='display: none;'>{this.mx_main_title(this.$t('billing.planform.title.zoomphone'),  undefined, item.parent_category)}</div>
      })
    },

    $renderPrice() {
      const hasPbxNoneBasePlans = plansof(this.selectedPbxPlans)
        .type(planTypes.ZOOM_PHONE)
        .not()
        .sub_type('BASE')
        .has();
      if (isBizPlusContext && !hasPbxNoneBasePlans) return; // isBizPlusContext unnecessary, only for descoping risks
      if (isProPlusContext) return; // PRO PLUS HARD, not consider pbx + pro plus mix buy case. For Pro plus context, need remove cart render, phone render will in pro plus context
      if (!this.selectedPbxPlans.length) return;
      return (
        <div class="opc-cart-item">
          {this.renderMainTitle()}
          {this.$renderCallingPlan()}
          {this.$renderTollFreePlan()}
          {this.$renderPNplan()}
          {this.$renderBasePlan()}
          <div class="pbx-cart-part">
            {this.pbxAddonPlans.map((item) => (
              <div key={item.plan_code}>
                {this.mx_subTitle(
                  this.$t(`billing.pbx.plan_name.${item.category}`),
                  this.mx_price(item),
                  item?.plan_code,
                )}
                {this.mx_discount(item.plan_code)}
                {this.mx_license(
                  item.cartInfo.selected_number,
                  this.$t(phonePlanUnitMap[item.type] || 'billing.planform.unit.number'),
                )}
                {this.mx_cycle(item.billing_cycle)}
                {this.mx_edit_addon(item)}
                {this.isShowRmvBtn && this.mx_remove(item)}
              </div>
            ))}
          </div>
        </div>
      );
    },
  },
};
</script>
<style lang="scss">
.pbx-cart-part {
  margin-bottom: 10px;
}
</style>
